/**
 * Widget data cache + refresh scheduler.
 *
 * One cache keyed by data key, so five tiles reading /api/dashboard/statistics/ cost one
 * request. A single 10 s ticker runs the fetchers that are due (per-key interval class),
 * pauses while the tab is hidden, and refreshes stale keys as soon as the tab is visible or
 * the window is focused. In-flight requests are deduped per key and guarded with a request
 * id so a slow old response never overwrites a newer one.
 */
import { create } from 'zustand';

export const INTERVALS = { live: 30000, normal: 60000, slow: 300000 };
const TICK_MS = 10000;
export const STALE_AFTER_MS = 5 * 60 * 1000;

// Module-level registry: not React state, so ticks never re-render anything by themselves.
const sources = new Map();
let ticker = null;
let tickerRefs = 0;
let generation = 0;

export const useDashboardDataStore = create(() => ({ entries: {} }));

const EMPTY_ENTRY = Object.freeze({ data: undefined, error: null, updatedAt: null, loading: false, refreshing: false });

export function selectEntry(key) {
  return (state) => state.entries[key] || EMPTY_ENTRY;
}

function setEntry(key, patch) {
  useDashboardDataStore.setState((state) => ({
    entries: { ...state.entries, [key]: { ...(state.entries[key] || EMPTY_ENTRY), ...patch } },
  }));
}

function intervalFor(source) {
  let fastest = Infinity;
  source.subscribers.forEach((intervalClass) => {
    fastest = Math.min(fastest, INTERVALS[intervalClass] || INTERVALS.normal);
  });
  return Number.isFinite(fastest) ? fastest : INTERVALS.normal;
}

export function run(key, { force = false } = {}) {
  const source = sources.get(key);
  if (!source) return Promise.resolve();
  if (source.inflight && !force) return source.inflight;
  const reqId = ++source.reqId;
  const myGeneration = generation;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  source.controller = controller;
  const current = useDashboardDataStore.getState().entries[key];
  setEntry(key, current?.data === undefined ? { loading: true } : { refreshing: true });
  const promise = Promise.resolve()
    .then(() => source.fetcher({ signal: controller?.signal }))
    .then((data) => {
      if (reqId !== source.reqId || myGeneration !== generation) return;
      setEntry(key, { data, error: null, updatedAt: Date.now(), loading: false, refreshing: false });
    })
    .catch((error) => {
      if (reqId !== source.reqId || myGeneration !== generation) return;
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      setEntry(key, { error, loading: false, refreshing: false });
    })
    .finally(() => {
      source.lastRun = Date.now();
      if (source.inflight === promise) source.inflight = null;
    });
  source.inflight = promise;
  return promise;
}

/** Register a subscriber for `key`; returns the unsubscribe function. */
export function subscribe(key, fetcher, intervalClass = 'normal') {
  let source = sources.get(key);
  if (!source) {
    source = { fetcher, subscribers: new Map(), inflight: null, reqId: 0, lastRun: 0, controller: null };
    sources.set(key, source);
  }
  source.fetcher = fetcher;
  const token = Symbol(key);
  source.subscribers.set(token, intervalClass);
  const entry = useDashboardDataStore.getState().entries[key];
  const due = !entry || entry.data === undefined || Date.now() - source.lastRun >= intervalFor(source);
  if (due && !source.inflight) run(key);
  return () => {
    const live = sources.get(key);
    if (!live) return;
    live.subscribers.delete(token);
  };
}

function isDue(source, now) {
  return source.subscribers.size > 0 && !source.inflight && now - source.lastRun >= intervalFor(source);
}

export function tick() {
  if (typeof document !== 'undefined' && document.hidden) return;
  const now = Date.now();
  sources.forEach((source, key) => {
    if (isDue(source, now)) run(key);
  });
}

export function refreshDue() {
  tick();
}

function onVisibility() {
  if (typeof document !== 'undefined' && !document.hidden) refreshDue();
}

export function startTicker() {
  tickerRefs += 1;
  if (ticker) return;
  ticker = setInterval(tick, TICK_MS);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
  if (typeof window !== 'undefined') window.addEventListener('focus', refreshDue);
}

export function stopTicker() {
  tickerRefs = Math.max(0, tickerRefs - 1);
  if (tickerRefs > 0 || !ticker) return;
  clearInterval(ticker);
  ticker = null;
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
  if (typeof window !== 'undefined') window.removeEventListener('focus', refreshDue);
}

function matches(key, prefix) {
  return key === prefix || key.startsWith(prefix);
}

/** Re-fetch every subscribed key matching a prefix now; forget unsubscribed ones. */
export function invalidate(prefixes) {
  const list = Array.isArray(prefixes) ? prefixes : [prefixes];
  const drop = [];
  sources.forEach((source, key) => {
    if (!list.some((prefix) => matches(key, prefix))) return;
    source.lastRun = 0;
    if (source.subscribers.size > 0) run(key, { force: true });
    else drop.push(key);
  });
  if (drop.length) {
    useDashboardDataStore.setState((state) => {
      const entries = { ...state.entries };
      drop.forEach((key) => delete entries[key]);
      return { entries };
    });
    drop.forEach((key) => sources.delete(key));
  }
}

/** Organization switch: abort everything, clear the cache, re-run what is still subscribed. */
export function reset() {
  generation += 1;
  sources.forEach((source) => {
    source.controller?.abort?.();
    source.inflight = null;
    source.lastRun = 0;
    source.reqId += 1;
  });
  useDashboardDataStore.setState({ entries: {} });
  sources.forEach((source, key) => {
    if (source.subscribers.size > 0) run(key);
  });
}

/** Test/debug helper. */
export function _sources() {
  return sources;
}
